/**
 * **滑块。**
 *
 * 印刷语言：轨道是一条细线，滑块是一根**竖条**（不是圆点）。
 *
 * ## 两个必须照抄的坑
 *
 * 1. **拖动靠 `input`（舞台）上的 `globalpointermove`。** 指针一旦离开滑块
 *    自己的范围，滑块上的 `pointermove` 就收不到了。这是 `handView.ts`
 *    已经踩过的同一件事，连"在画布外松开"的兜底也照抄了
 *    （`buttons === 0` 就当已经松手）。
 * 2. **必须 `destroy()`。** 那三个监听器挂在舞台上，容器销毁不会摘掉它们；
 *    不摘的话每次重建菜单都会留下一份幽灵监听器。
 *
 * 状态反馈仍然走 `state.ts`（hover 加印、按下压印），但**禁用只撤墨、不划杠**：
 * 一道横贯 400px 的斜线会读成渲染错误，而不是"作废"。
 */

import { Container, Graphics, Rectangle } from 'pixi.js';
import type { FederatedPointerEvent } from 'pixi.js';
import { INK, uiLine, uiPad } from './style';
import { createSurface, drawStamp, type ControlMotion, type ControlState } from './state';
import { stampInkAt } from './ink';
import { richLabel } from './richText';
import { formatNumber } from './format';
import type { Widget } from './base';

export interface SliderSpec {
  readonly label: string;
  readonly scale: number;
  readonly min: number;
  readonly max: number;
  readonly step: number;
  readonly value: number;
  /** 拖动过程中**每一帧**都会调。要 live 预览就用它 */
  readonly onInput?: (value: number) => void;
  /**
   * 松手时调一次。
   *
   * ⚠️ 这个回调是**必须**有的：改 UI 倍率这类"改了要重建界面"的设置，
   * 只能在松手时生效 —— 在拖动中就重建，会把正在拖的那个滑块本身销毁掉，
   * 手一松就断在半路。
   */
  readonly onCommit?: (value: number) => void;
  readonly format?: (value: number) => string;
  /** 把值换算成 0..1 的位置。默认按 min/max 线性 */
  readonly toFraction?: (value: number) => number;
  /** 反过来。默认线性 */
  readonly fromFraction?: (fraction: number) => number;
  readonly width?: number;
  readonly enabled?: boolean;
  readonly input: Container;
}

export interface Slider extends Widget {
  setValue(value: number): void;
  setEnabled(enabled: boolean): void;
  readonly value: number;
  /**
   * ⚠️ **必须调。** 见文件头第 2 条。
   */
  destroy(): void;
}

export function slider(spec: SliderSpec): Slider {
  const scale = spec.scale;
  const trackHeight = Math.max(2, uiLine(1.6, scale));
  const knobWidth = uiPad(6, scale);
  const knobHeight = uiPad(18, scale);
  const width = spec.width ?? 260;
  const labelWidth = uiPad(96, scale);
  const valueWidth = uiPad(56, scale);
  const trackX = labelWidth;
  const trackWidth = Math.max(40, width - labelWidth - valueWidth);
  const height = Math.max(knobHeight, uiPad(20, scale));
  const trackY = Math.round((height - trackHeight) / 2);
  const knobY = Math.round((height - knobHeight) / 2);

  const container = new Container();
  const name = richLabel({ text: spec.label, scale, size: 12, fill: INK.textDim, role: 'cjk' });
  const track = new Graphics();
  const fill = new Graphics();
  const knob = new Graphics();
  const readout = richLabel({
    text: '',
    scale,
    size: 11,
    fill: INK.text,
    role: 'mono',
    align: 'right',
  });

  container.addChild(name, track, fill, knob, readout);
  name.position.set(0, Math.round((height - name.height) / 2));
  readout.anchor.set(1, 0);
  readout.position.set(width, Math.round((height - readout.height) / 2));

  let value = spec.value;
  let dragging = false;

  const fraction = (): number => {
    if (spec.toFraction !== undefined) return clamp01(spec.toFraction(value));
    const span = spec.max - spec.min;
    return span === 0 ? 0 : clamp01((value - spec.min) / span);
  };

  const surface = createSurface({
    container,
    width: labelWidth + trackWidth,
    height,
    ...(spec.enabled === undefined ? {} : { enabled: spec.enabled }),
    // 只有轨道那一条能点 —— 点标签不该跳值
    hitArea: new Rectangle(trackX, 0, trackWidth, height),
    onPressStart: () => {
      dragging = true;
    },
    onStateChange: (state, motion) => paint(state, motion),
    onFrame: (state, motion) => paint(state, motion),
  });

  function paint(state: ControlState, motion: ControlMotion): void {
    const f = fraction();
    const knobX = trackX + f * trackWidth;

    // 轨道：细线。悬停时亮一档（也是"加墨"）
    track
      .clear()
      .rect(trackX, trackY, trackWidth, trackHeight)
      .fill({ color: INK.rule });
    track.alpha = state === 'disabled' ? 0.45 : state === 'idle' ? 0.7 : 1;

    // 已选中的那一段：实心酸色，**禁用时撤掉**
    const ink = stampInkAt('primary', state, motion.mix);
    fill.clear();
    if (state !== 'disabled') {
      fill.rect(trackX, trackY, f * trackWidth, trackHeight).fill({ color: ink.fill });
    }

    // 滑块：一根竖条，不是圆点。悬停时跟着整块抬起来
    knob.clear();
    if (state === 'disabled') {
      knob.rect(knobX - knobWidth / 2, knobY, knobWidth, knobHeight).fill({ color: INK.rule });
    } else {
      drawStamp(
        knob,
        ink,
        { x: knobX - knobWidth / 2, y: knobY, width: knobWidth, height: knobHeight },
        scale,
        motion.lift,
      );
    }

    readout.text = spec.format === undefined ? formatNumber(value) : spec.format(value);
    readout.style.fill = state === 'disabled' ? INK.textDim : INK.text;
    // 整行跟着抬：只有滑块头动、字不动会读成"滑块脱开了"
    name.y = Math.round((height - name.height) / 2) + motion.lift;
    readout.y = Math.round((height - readout.height) / 2) + motion.lift;
  }

  const setFromLocal = (localX: number, commit: boolean): void => {
    const f = clamp01((localX - trackX) / trackWidth);
    const raw = spec.fromFraction === undefined ? spec.min + f * (spec.max - spec.min) : spec.fromFraction(f);
    const snapped = spec.step > 0 ? Math.round(raw / spec.step) * spec.step : raw;
    const next = clamp(snapped, spec.min, spec.max);
    // 浮点误差会把 0.30000000000000004 写成显示值，先收一下
    const cleaned = Math.abs(next) < 1e-9 ? 0 : next;
    if (cleaned !== value) {
      value = cleaned;
      paint(surface.state, surface.motion);
      spec.onInput?.(value);
    }
    if (commit) spec.onCommit?.(value);
  };

  const onMove = (e: FederatedPointerEvent): void => {
    if (!dragging) return;
    // 兜底：在画布外松开时 `pointerup` 收不到，滑块会一直粘着指针
    if (e.buttons === 0) {
      dragging = false;
      spec.onCommit?.(value);
      return;
    }
    setFromLocal(container.toLocal(e.global).x, false);
  };
  const onUp = (): void => {
    if (!dragging) return;
    dragging = false;
    spec.onCommit?.(value);
  };

  // ⚠️ 按下时先跳一次值：点轨道任意位置应该立刻过去，不用先拖
  container.on('pointerdown', (e: FederatedPointerEvent) => {
    if (!surface.enabled) return;
    setFromLocal(container.toLocal(e.global).x, false);
  });
  spec.input.on('globalpointermove', onMove);
  spec.input.on('pointerup', onUp);
  spec.input.on('pointerupoutside', onUp);
  paint(surface.state, surface.motion);

  return {
    container,
    width,
    height,
    get value() {
      return value;
    },
    setValue(next: number) {
      value = clamp(next, spec.min, spec.max);
      paint(surface.state, surface.motion);
    },
    setEnabled(next: boolean) {
      if (!next) dragging = false;
      surface.setEnabled(next);
    },
    destroy() {
      surface.destroy();
      spec.input.off('globalpointermove', onMove);
      spec.input.off('pointerup', onUp);
      spec.input.off('pointerupoutside', onUp);
      container.destroy({ children: true });
    },
  };
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return value < min ? min : value > max ? max : value;
}

function clamp01(value: number): number {
  return clamp(value, 0, 1);
}
